// The runner promote REQUEST's dashboard write (#6735): a platform owner asks for the
// promote of a soaked canary runner build to stable
// (`POST /v1/runners/releases/:version/promote`). The platform records who asked on
// its own audit log, then starts the release run that publishes it.
//
// ON AN OPERATOR CREDENTIAL, NEVER THE SESSION. The route rides `admin:releases`,
// which no role ceiling and no session carries; `withOperatorCredential` asks the door
// for a one-hour credential with exactly that scope (`./operator-credential.ts`).
//
// A ROUTE REFUSAL IS AN ANSWER, not a fault: `not_on_canary`, `soak_too_short`,
// `already_promoted`, `promote_in_flight` and the rest come back by their `reason`
// with the problem's members, so the section says each in its own words.

import { t } from '../i18n';
import { ApiError } from './api';
import { type OperatorOutcome, withOperatorCredential } from './operator-credential';
import { ago, untilLabel } from './time';

/** The route's 202. */
export type PromoteAccepted = {
  readonly request_id: string;
  readonly version: string;
  readonly from: 'canary';
  readonly to: 'stable';
  readonly dispatched: true;
  readonly runs_url: string;
};

export type PromoteAnswer =
  | { readonly kind: 'accepted'; readonly body: PromoteAccepted }
  | {
      readonly kind: 'refused';
      readonly reason: string;
      readonly extensions: Readonly<Record<string, unknown>>;
    };

/** The statuses the route answers a named refusal on (409 / 422 / 502 / 503). */
const REFUSAL_STATUSES: ReadonlySet<number> = new Set([409, 422, 502, 503]);

/**
 * The route path. `+` stays literal — it is a legal path character, and the version
 * reads as itself in the trail — while anything else is encoded.
 */
export const promotePath = (version: string): string =>
  `/v1/runners/releases/${encodeURIComponent(version).replace(/%2B/gi, '+')}/promote`;

/** Ask for the promote of `version`. The door's refusal sends nothing. */
export function requestRunnerPromote(
  version: string,
  token: string | null,
): Promise<OperatorOutcome<PromoteAnswer>> {
  return withOperatorCredential(
    { scopes: ['admin:releases'], name: 'fleet: promote a canary runner build', token },
    async (send): Promise<PromoteAnswer> => {
      try {
        const body = await send<PromoteAccepted>(promotePath(version), {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          // The move is stated, never defaulted: the route takes both ends as literals.
          body: JSON.stringify({ from: 'canary', to: 'stable' }),
        });
        return { kind: 'accepted', body };
      } catch (err) {
        const reason = err instanceof ApiError ? err.extensions?.['reason'] : undefined;
        if (!(err instanceof ApiError) || !REFUSAL_STATUSES.has(err.status)) throw err;
        if (typeof reason !== 'string') throw err;
        return { kind: 'refused', reason, extensions: err.extensions ?? {} };
      }
    },
  );
}

const str = (v: unknown): string => (typeof v === 'string' ? v : '');

/** What the section says for an answer. `now` is the one clock reading it renders from. */
export function promoteMessage(version: string, answer: PromoteAnswer, now: number): string {
  if (answer.kind === 'accepted') {
    return t('fleet.promote.result', { version, request_id: answer.body.request_id });
  }
  const x = answer.extensions;
  switch (answer.reason) {
    case 'not_on_canary':
      return t('fleet.promote.refused_not_on_canary', { version });
    case 'soak_too_short':
      return t('fleet.promote.refused_soak_too_short', {
        version,
        remaining: untilLabel(str(x['retry_at']), now),
      });
    case 'already_promoted':
      return t('fleet.promote.refused_already_promoted', { version });
    case 'promote_in_flight':
      return t('fleet.promote.refused_in_flight', {
        version,
        ago: ago(str(x['requested_at']), now),
      });
    case 'dispatch_unknown':
      // The request was sent and GitHub's answer was lost: the run may be going, and the
      // request holds the version, so "Nothing was started" would be false here.
      return t('fleet.promote.dispatch_unknown', {
        version,
        request_id: str(x['promote_request_id']),
      });
    default:
      return t('fleet.promote.failed', { reason: answer.reason });
  }
}
